import { useState } from 'react';
import { api, ApiError, formatDateTime } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { Appointment, AppointmentStatus, Facility, Page } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Modal, PageHeader, Spinner, StatusBadge } from '../../ui/components';

const STATUSES: AppointmentStatus[] = ['BOOKED', 'CANCELLED', 'COMPLETED', 'NO_SHOW'];

/**
 * Appointment lifecycle management — packages/modules/m24-ehealth/src/routes/appointments.ts.
 * POST /appointments (booking) requires the caller's OWN verified NIN
 * (requireTier('T2'), no personId-style "book on behalf" field like
 * c10-servicedesk's appointments) — a staff member cannot book a new
 * appointment for a citizen here; only the citizen books, via their own
 * app. This screen covers everything staff DO own: the list, lifecycle
 * transitions and the teleconsult session handle. See this app's build
 * report for the full reasoning.
 */
export function Appointments() {
  const { persona } = useAuth();
  const isReception = persona?.roles.includes('RECEPTION') ?? false;
  const isClinicianOrReception = (persona?.roles.includes('CLINICIAN') || isReception) ?? false;

  const facilitiesQ = useApiQuery<Page<Facility>>('/api/v1/m24/facilities?limit=100');
  const [facilityId, setFacilityId] = useState('');
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [actionError, setActionError] = useState<string | null>(null);
  const [detail, setDetail] = useState<Appointment | null>(null);

  const qs = new URLSearchParams({ limit: '50' });
  if (facilityId) qs.set('facilityId', facilityId);
  if (status) qs.set('status', status);
  if (q) qs.set('q', q);
  const listQ = useApiQuery<Page<Appointment>>(`/api/v1/m24/appointments?${qs.toString()}`);

  const act = async (a: Appointment, action: 'cancel' | 'complete' | 'no-show' | 'teleconsult/issue') => {
    setActionError(null);
    try {
      await api.post(`/api/v1/m24/appointments/${a.id}/${action}`);
      listQ.reload();
      if (detail?.id === a.id) {
        const fresh = await api.get<Appointment>(`/api/v1/m24/appointments/${a.id}`);
        setDetail(fresh);
      }
    } catch (e) {
      setActionError(e instanceof ApiError ? `${e.message} (${e.code})` : `Could not ${action.replace('/', ' ')}`);
    }
  };

  return (
    <div>
      <PageHeader
        title="Appointments"
        subtitle="Staff manage the lifecycle here; citizens book new appointments themselves via facility open slots."
      />

      {!isClinicianOrReception ? (
        <div className="mb-4">
          <ErrorNote message="Listing appointments requires CLINICIAN or RECEPTION." />
        </div>
      ) : null}
      {actionError ? (
        <div className="mb-4">
          <ErrorNote message={actionError} />
        </div>
      ) : null}

      <div className="mb-4 flex flex-wrap gap-2">
        <select className="field-input !w-56" value={facilityId} onChange={(e) => setFacilityId(e.target.value)}>
          <option value="">All facilities</option>
          {(facilitiesQ.data?.items ?? []).map((f) => (
            <option key={f.id} value={f.id}>
              {f.name}
            </option>
          ))}
        </select>
        <select className="field-input !w-44" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All statuses</option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {s.replaceAll('_', ' ')}
            </option>
          ))}
        </select>
        <input className="field-input !w-48" placeholder="Search NIN…" value={q} onChange={(e) => setQ(e.target.value)} />
      </div>

      {listQ.error ? <ErrorNote message={listQ.error} /> : null}

      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No appointments match" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Patient (NIN)</th>
                <th className="table-head">Facility</th>
                <th className="table-head">Specialty</th>
                <th className="table-head">Slot</th>
                <th className="table-head">Status</th>
                <th className="table-head" />
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((a) => (
                <tr key={a.id} className="hover:bg-slate-50">
                  <td className="table-cell font-medium">{a.nin}</td>
                  <td className="table-cell">{a.facility?.name ?? a.facilityId}</td>
                  <td className="table-cell">
                    {a.specialty}
                    {a.teleconsult ? <span className="ml-1 text-xs text-indigo-600">📹</span> : null}
                  </td>
                  <td className="table-cell text-xs">{formatDateTime(a.slot?.startsAt)}</td>
                  <td className="table-cell">
                    <StatusBadge status={a.status} />
                  </td>
                  <td className="table-cell text-right">
                    <div className="flex justify-end gap-1.5">
                      <button className="btn-secondary !px-2 !py-1 text-xs" onClick={() => setDetail(a)}>
                        View
                      </button>
                      {a.status === 'BOOKED' ? (
                        <>
                          <button className="btn-success !px-2 !py-1 text-xs" onClick={() => act(a, 'complete')} disabled={!isClinicianOrReception}>
                            Complete
                          </button>
                          <button className="btn-danger !px-2 !py-1 text-xs" onClick={() => act(a, 'no-show')} disabled={!isReception}>
                            No-show
                          </button>
                        </>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {detail ? (
        <Modal title={`Appointment — ${detail.specialty}`} onClose={() => setDetail(null)}>
          <div className="space-y-3 text-sm">
            <div className="flex items-center justify-between">
              <StatusBadge status={detail.status} />
              {detail.teleconsult ? <span className="text-xs font-semibold text-indigo-600">Teleconsult</span> : null}
            </div>
            <dl className="space-y-1.5">
              <div className="flex justify-between">
                <dt className="text-slate-500">Patient NIN</dt>
                <dd className="font-medium">{detail.nin}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-slate-500">Facility</dt>
                <dd className="font-medium">{detail.facility?.name ?? detail.facilityId}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-slate-500">Slot</dt>
                <dd className="font-medium">{formatDateTime(detail.slot?.startsAt)}</dd>
              </div>
              {detail.referralId ? (
                <div className="flex justify-between">
                  <dt className="text-slate-500">Referral</dt>
                  <dd className="font-mono text-xs">{detail.referralId}</dd>
                </div>
              ) : null}
              {detail.cancelReason ? (
                <div className="flex justify-between">
                  <dt className="text-slate-500">Cancel reason</dt>
                  <dd className="font-medium">{detail.cancelReason}</dd>
                </div>
              ) : null}
            </dl>

            {detail.teleconsult && detail.status === 'BOOKED' ? (
              <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-3">
                <div className="text-xs font-semibold uppercase tracking-wide text-indigo-700">Teleconsult session</div>
                {detail.teleconsultUrl ? (
                  <a href={detail.teleconsultUrl} target="_blank" rel="noreferrer" className="mt-1 block break-all text-xs font-medium text-indigo-700 underline">
                    {detail.teleconsultUrl}
                  </a>
                ) : (
                  <div className="mt-1 text-xs text-indigo-700">No session issued yet.</div>
                )}
                <button
                  className="btn-secondary mt-2 !bg-white !px-2.5 !py-1 text-xs"
                  onClick={() => act(detail, 'teleconsult/issue')}
                  disabled={!isClinicianOrReception}
                >
                  {detail.teleconsultUrl ? 'Rotate session link' : 'Issue session link'}
                </button>
              </div>
            ) : null}

            {detail.status === 'BOOKED' ? (
              <div className="flex flex-wrap gap-1.5 border-t border-slate-100 pt-3">
                <button className="btn-success !px-2.5 !py-1 text-xs" onClick={() => act(detail, 'complete')} disabled={!isClinicianOrReception}>
                  Mark completed
                </button>
                <button className="btn-danger !px-2.5 !py-1 text-xs" onClick={() => act(detail, 'no-show')} disabled={!isReception}>
                  Mark no-show
                </button>
                <button className="btn-secondary !px-2.5 !py-1 text-xs" onClick={() => act(detail, 'cancel')}>
                  Cancel
                </button>
              </div>
            ) : null}
          </div>
        </Modal>
      ) : null}
    </div>
  );
}
